import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Avatar,
  Button,
  Alert,
  Collapse,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  MenuItem,
  Checkbox,
} from "@mui/material";
import {
  CheckCircle,
  Article,
  InfoOutlined,
  Description,
  Settings,
  ExpandMore,
} from "@mui/icons-material";
import { FAST_API_BASE_URL } from "constants";
import { useAuth } from "context/AuthContext";
import { useSettings } from "context/SettingsContext";
import useHubspotStatus from "./hooks/useHubspotStatus";
import HubSpotDataDisplay from "./components/HubSpotDataDisplay";
import SoftSelect from "components/SoftSelect";

const HubSpotIcon = () => (
  <svg fill="#219297" width="131px" height="131px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" stroke="#219297">
    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      <title>HubSpot icon</title>
      <path d="M18.164 7.931V5.085a2.198 2.198 0 0 0 1.266-1.978V3.04A2.199 2.199 0 0 0 17.238.847h-.067a2.199 2.199 0 0 0-2.193 2.192v.067a2.196 2.196 0 0 0 1.252 1.973l.013.006v2.852a6.22 6.22 0 0 0-2.969 1.31l.012-.009-7.828-6.096a2.497 2.497 0 1 0-1.157 1.515l-.012.006 7.696 5.991a6.176 6.176 0 0 0-1.038 3.446c0 1.343.425 2.588 1.147 3.606l-.013-.019-2.342 2.342a1.968 1.968 0 0 0-.58-.095h-.002a2.033 2.033 0 1 0 2.033 2.033 1.978 1.978 0 0 0-.099-.595l.004.014 2.317-2.317a6.247 6.247 0 1 0 4.782-11.133l-.036-.005zm-.964 9.377a3.206 3.206 0 1 1 3.214-3.206v.002a3.206 3.206 0 0 1-3.206 3.206z"></path>
    </g>
  </svg>
);

const HubSpotIntegration = () => {
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;

  // Use SettingsContext
  const { 
    settings, 
    updateSettings, 
    loading: settingsLoading,
    refreshSettings 
  } = useSettings();

  const {
    hasHubspotCredentials,
    checkHubspotCredentials,
    setHasHubspotCredentials,
  } = useHubspotStatus(domainId);

  const [oauthLoading, setOauthLoading] = useState(false);
  const [isRemovingAccount, setIsRemovingAccount] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [selectedDataType, setSelectedDataType] = useState(null);
  
  // Settings state
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [syncFrequency, setSyncFrequency] = useState("1 week");
  const [tempSyncFrequency, setTempSyncFrequency] = useState("1 week");
  const [autoSyncNewContent, setAutoSyncNewContent] = useState(false);
  const [tempAutoSyncNewContent, setTempAutoSyncNewContent] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
  });

  const HUBSPOT_VIDEO_URL = "https://www.loom.com/embed/your-hubspot-video-id";

  const dataTypes = [
    { id: "blogs", label: "Blogs", icon: <Article /> },
    { id: "pages", label: "Website Pages", icon: <Description /> },
    { id: "landing-pages", label: "Landing Pages", icon: <Description /> },
  ];

  const syncFrequencyOptions = [
    { value: "1 day", label: "1 day" },
    { value: "1 week", label: "7 days" },
    { value: "1 month", label: "30 days" },
  ];

  // Load sync frequency from settings context
  useEffect(() => {
    if (settings?.hubspot_sync_interval) {
      const savedFrequency = settings.hubspot_sync_interval;
      setSyncFrequency(savedFrequency);
      setTempSyncFrequency(savedFrequency);
    }
    if (settings?.autosync_hubspot_new_content !== undefined) {
      setAutoSyncNewContent(settings.autosync_hubspot_new_content);
      setTempAutoSyncNewContent(settings.autosync_hubspot_new_content);
    }
  }, [settings?.hubspot_sync_interval, settings?.autosync_hubspot_new_content]);

  useEffect(() => {
    const checkStatus = async () => {
      setIsCheckingStatus(true);
      await checkHubspotCredentials();
      setIsCheckingStatus(false);
    };

    if (domainId) {
      checkStatus();
    }
  }, [domainId, checkHubspotCredentials]);

  useEffect(() => {
    const handleMessage = (event) => {
      const allowedOrigins = [
        window.location.origin,
        'http://localhost:8000',
        'https://api.ansera.ai',
      ];

      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data.type === 'HUBSPOT_AUTH_SUCCESS') {
        setHasHubspotCredentials(true);
        setOauthLoading(false);
        setSnackbar({
          open: true,
          severity: "success",
          message: "HubSpot connected successfully!",
        });
        // Refresh settings after successful connection
        refreshSettings();
      } else if (event.data.type === 'HUBSPOT_AUTH_ERROR') {
        setOauthLoading(false);
        setSnackbar({
          open: true,
          severity: "error",
          message: event.data.message || "HubSpot authorization failed",
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [setHasHubspotCredentials, refreshSettings]);

  const handleConnectHubSpot = async () => {
    setOauthLoading(true);

    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/hubspot/auth`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });

      const data = await response.json();

      if (data.resultType === "Success" && data.result.auth_url) {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          data.result.auth_url,
          "HubSpot OAuth",
          `width=${width},height=${height},left=${left},top=${top}`
        );
      } else {
        setOauthLoading(false);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to initiate HubSpot authorization",
        });
      }
    } catch (error) {
      setOauthLoading(false);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to connect to HubSpot",
      });
    }
  };

  const handleRemoveAccount = async () => {
    setIsRemovingAccount(true);

    try {
      const response = await fetch(
        `${FAST_API_BASE_URL}/api/mcp-credentials/hubspot`,
        {
          method: "DELETE",
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );

      const data = await response.json();

      if (data.resultType === "Success") {
        setHasHubspotCredentials(false);
        setSelectedDataType(null);
        setSnackbar({
          open: true,
          severity: "success",
          message: "HubSpot account disconnected successfully",
        });
        // Refresh settings after disconnection
        refreshSettings();
      } else {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to disconnect HubSpot account",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to disconnect HubSpot account",
      });
    } finally {
      setIsRemovingAccount(false);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleDataTypeSelect = (dataType) => {
    setSelectedDataType(dataType);
  };

  const handleEditFrequency = () => {
    setTempSyncFrequency(syncFrequency);
    setTempAutoSyncNewContent(autoSyncNewContent);
    setIsEditingFrequency(true);
  };

  const handleCancelEdit = () => {
    setTempSyncFrequency(syncFrequency);
    setTempAutoSyncNewContent(autoSyncNewContent);
    setIsEditingFrequency(false);
  };
  const handleSaveSettings = async () => {
    setIsSavingSettings(true);

    try {
      // Use the SettingsContext updateSettings method
      await updateSettings({
        hubspot_sync_interval: tempSyncFrequency,
        autosync_hubspot_new_content: tempAutoSyncNewContent,
      });

      setSyncFrequency(tempSyncFrequency);
      setAutoSyncNewContent(tempAutoSyncNewContent);
      setIsEditingFrequency(false);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Settings saved successfully",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: error.message || "Failed to save settings",
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const renderConnectionPrompt = () => (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: "12px",
        border: "1px solid #e0f0f0",
        backgroundColor: "#ffffff",
        textAlign: "center",
      }}
    >
      <Typography variant="h5" fontWeight="600" sx={{ color: "#219297", mb: 1 }}>
        Connect HubSpot
      </Typography>
      
      <Typography variant="body1" sx={{ color: "#666", mb: 3, maxWidth: 400, margin: '0 auto' }}>
        Sync your blogs, website pages, and landing pages for enhanced search capabilities
      </Typography>

      <Button
        variant="contained"
        onClick={handleConnectHubSpot}
        disabled={oauthLoading}
        sx={{
          backgroundColor: "#219297",
          color: "#ffffff",
          fontWeight: 600,
          py: 1.5,
          px: 1.5,
          borderRadius: "8px",
          textTransform: "none",
          fontSize: "1rem",
          "&:hover": {
            backgroundColor: "#1a767a",
          },
        }}
      >
        {oauthLoading ? "Connecting..." : "Connect HubSpot Account"}
      </Button>
    </Paper>
  );

  const renderConnectedStatus = () => (
    <Box sx={{ mb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "10px",
          backgroundColor: "#f0f9fb",
          border: "1px solid #b8dfd0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              backgroundColor: "#ffffff",
              border: "1px solid #b8dfd0",
            }}
          >
            <HubSpotIcon />
          </Avatar>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CheckCircle sx={{ color: "#219297", fontSize: 20 }} />
              <Typography variant="h6" sx={{ color: "#219297", fontWeight: 600 }}>
                HubSpot Connected
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Button
          variant="outlined"
          onClick={() => setConfirmDialog({ open: true, action: "remove-account" })}
          disabled={isRemovingAccount}
          sx={{
            borderColor: "#da0000",
            color: "#cc0a0a",
            textTransform: "none",
            fontSize: "0.875rem",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#fff5f5",
              borderColor: "#bb2626",
            },
          }}
        >
          {isRemovingAccount ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {isRemovingAccount ? "Disconnecting..." : "Disconnect"}
        </Button>
      </Paper>
    </Box>
  );

  const renderDataSelector = () => {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: "#333", mb: 1, fontWeight: 600 }}>
            Select Data Type
          </Typography>
          
          <SoftSelect
            options={dataTypes.map((dataType) => ({
              value: dataType.id,
              label: dataType.label,
              icon: dataType.icon,
            }))}
            placeholder="Choose a data type..."
            value={
              selectedDataType
                ? {
                    value: selectedDataType,
                    label: dataTypes.find((dt) => dt.id === selectedDataType)?.label || '',
                  }
                : null
            }
            onChange={({ value }) => handleDataTypeSelect(value)}
            sx={{
              width: '100%',
              maxWidth: 400,
            }}
          />
        </Box>
        
        {selectedDataType && (
          <HubSpotDataDisplay
            dataType={selectedDataType}
            domainId={domainId}
          />
        )}
      </Box>
    );
  };

  const renderOtherSettings = () => (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "12px",
        backgroundColor: "#ffffff",
        border: "1px solid #e0f0f0",
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Box
        onClick={() => setSettingsExpanded(!settingsExpanded)}
        sx={{
          p: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#f8fafa",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Settings sx={{ color: "#219297", fontSize: 24 }} />
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            Other Settings
          </Typography>
        </Box>
        <ExpandMore
          sx={{
            color: "#666",
            transform: settingsExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
          }}
        />
      </Box>

      <Collapse in={settingsExpanded}>
        <Box sx={{ p: 2.5, pt: 0, borderTop: "1px solid #f0f0f0" }}>
          <Box sx={{ mb: 2, p: 2 }}>
            <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
              Sync HubSpot data for every{" "}
              {!isEditingFrequency ? (
                <Typography
                  component="span"
                  onClick={handleEditFrequency}
                  sx={{
                    color: "#219297",
                    fontWeight: 700,
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      color: "#1a767a",
                    },
                  }}
                >
                  {syncFrequency}
                </Typography>
              ) : (
                <FormControl
                  size="small"
                  sx={{
                    display: "inline-flex",
                    minWidth: 120,
                    mx: 0.5,
                    verticalAlign: "middle",
                  }}
                >
                  <Select
                    value={tempSyncFrequency}
                    onChange={(e) => setTempSyncFrequency(e.target.value)}
                    sx={{
                      color: "#219297",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#219297",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1a767a",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#219297",
                      },
                    }}
                  >
                    {syncFrequencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Typography>
          </Box>

          <Box sx={{ px: 2, pb: 1, display: "flex", alignItems: "flex-start", gap: 1.5, mt:-2 }}>
              <Checkbox
                checked={isEditingFrequency ? tempAutoSyncNewContent : autoSyncNewContent}
                onChange={(e) => {
                  if (!isEditingFrequency) {
                    setTempAutoSyncNewContent(e.target.checked);
                    setIsEditingFrequency(true);
                  } else {
                    setTempAutoSyncNewContent(e.target.checked);
                  }
                }}
                sx={{
                  color: "#219297",
                  mt: "-2px",
                  "&.Mui-checked": { color: "#219297" },
                }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#333" }}>
                  Sync new content automatically
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", fontSize: "0.8rem" }}>
                  System will periodically check for new content and sync it to the database automatically.
                </Typography>
              </Box>
          </Box>

          {isEditingFrequency && (
            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelEdit}
                disabled={isSavingSettings}
                sx={{
                  textTransform: "none",
                  borderColor: "#ccc",
                  color: "#666",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#219297",
                  color: "#ffffff",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#1a767a",
                  },
                }}
              >
                {isSavingSettings ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );

  const renderInstructions = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "12px",
        backgroundColor: "#f8fafa",
        border: "1px solid #e0f0f0",
        height: "fit-content",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <InfoOutlined sx={{ color: "#219297" }} />
        <Typography variant="h6" sx={{ color: "#219297", fontWeight: 600 }}>
          How It Works
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#219297",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.875rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            1
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Connect Account
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
              Authorize access to your HubSpot account
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#219297",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.875rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            2
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Select Data Type
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
              Choose between blogs, website pages, landing pages and other contents.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#219297",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.875rem",
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            3
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Select Content
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
              Choose specific items to sync for search
            </Typography>
          </Box>
        </Box>
      </Box>

      <Collapse in={showVideo}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "56.25%",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#000",
            mb: 2,
          }}
        >
          <iframe
            src={HUBSPOT_VIDEO_URL}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="HubSpot Setup Guide"
          />
        </Box>
      </Collapse>
    </Paper>
  );

  if (isCheckingStatus || settingsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: "#219297" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Collapse in={snackbar.open}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            mb: 3,
            borderRadius: "8px",
            backgroundColor: snackbar.severity === "success" ? "#f0f9fb" : undefined,
          }}
        >
          {snackbar.message}
        </Alert>
      </Collapse>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null })}
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 },
        }}
      >
        <DialogTitle sx={{ color: "#333", fontWeight: 600, pb: 1 }}>
          Disconnect HubSpot?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Are you sure you want to disconnect your HubSpot account? This will remove all synced data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null })}
            sx={{
              color: "#666",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveAccount}
            disabled={isRemovingAccount}
            sx={{
              backgroundColor: "#da0000",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f30303",
              },
            }}
          >
            {isRemovingAccount ? "Disconnecting..." : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>

      {!hasHubspotCredentials ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {renderConnectionPrompt()}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderInstructions()}
          </Grid>
        </Grid>
      ) : (
        <Box>
          {renderConnectedStatus()}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {renderOtherSettings()}
              {renderDataSelector()}
            </Grid>
            <Grid item xs={12} md={4}>
              {renderInstructions()}
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default HubSpotIntegration;